/**
 * InfoGatheringPhase — shared Phase 1 component for both Health "Mode Guidé" and "Mode Simulation".
 *
 * Fields are intentionally OPTIONAL: students use the RAO Emergensys which already contains
 * caller/patient info. Phase 1 here acts as an optional checklist; the Continue button is
 * always enabled.
 *
 * Props:
 *  - phase: protocol phase object with `id` and `fields[]`
 *  - answers: object mapping fieldId -> string
 *  - setAnswers: updater function (supports functional setState)
 *  - onComplete: callback when the user clicks Continue
 *  - compact: when true, renders the inline mini-variant used inside HealthSimulation chat panel
 */
import { ChevronRight } from 'lucide-react';

const InfoGatheringPhase = ({ phase, answers, setAnswers, onComplete, compact = false }) => {
  if (compact) {
    return (
      <div className="space-y-2" data-testid={`phase-${phase.id}`}>
        <div className="p-2 rounded text-[11px] text-blue-900 bg-blue-50 border border-blue-200" data-testid="sim-phase1-rao-notice">
          <strong>RAO Emergensys :</strong> Les informations sont déjà saisies dans la RAO. Champs optionnels.
        </div>
        <p className="text-xs text-gray-500 italic">Vous pouvez noter ces informations si utile :</p>
        {phase.fields.map(f => (
          <div key={f.id} className="flex items-center gap-2">
            <input
              type="text"
              placeholder={f.label}
              value={answers[f.id] || ''}
              onChange={e => setAnswers(prev => ({ ...prev, [f.id]: e.target.value }))}
              className="flex-1 px-2 py-1.5 border border-gray-200 rounded text-xs focus:outline-none focus:ring-1"
              data-testid={`sim-field-${f.id}`}
            />
          </div>
        ))}
        <button
          onClick={onComplete}
          className="w-full py-1.5 rounded text-xs font-semibold text-white hover:opacity-90"
          style={{ backgroundColor: '#002768' }}
          data-testid="sim-phase-next-btn"
        >
          Suivant <ChevronRight className="inline w-3 h-3" />
        </button>
      </div>
    );
  }

  return (
    <div className="space-y-3" data-testid={`phase-${phase.id}`}>
      <div className="p-2.5 rounded-lg text-xs text-blue-900 bg-blue-50 border border-blue-200" data-testid="phase1-rao-notice">
        <strong>RAO Emergensys :</strong> Les informations d'appel sont déjà saisies dans la RAO. Vous pouvez continuer sans remplir ces champs.
      </div>
      {phase.fields.map(field => (
        <div key={field.id}>
          <label className="text-sm font-semibold text-gray-700">
            {field.label} <span className="text-gray-400 text-xs">(optionnel)</span>
          </label>
          {field.detail && <p className="text-xs text-gray-400">{field.detail}</p>}
          <input
            type="text"
            value={answers[field.id] || ''}
            onChange={e => setAnswers(prev => ({ ...prev, [field.id]: e.target.value }))}
            className="mt-1 w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2"
            style={{ '--tw-ring-color': '#2E6CF5' }}
            data-testid={`field-${field.id}`}
          />
        </div>
      ))}
      <button
        onClick={onComplete}
        className="w-full mt-2 py-2.5 rounded-lg font-semibold text-white transition-all hover:opacity-90"
        style={{ backgroundColor: '#002768' }}
        data-testid="phase-next-btn"
      >
        Continuer <ChevronRight className="inline w-4 h-4 ml-1" />
      </button>
    </div>
  );
};

export default InfoGatheringPhase;
